import { Node, mergeAttributes } from '@tiptap/core'
import { ReactNodeViewRenderer } from '@tiptap/react'
import type { MarkdownNodeSpec } from 'tiptap-markdown'
import WikiLinkView from './WikiLinkView'

export interface WikiLinkOptions {
  onNavigate?: (noteId: string) => void
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    wikiLink: {
      insertWikiLink: (noteId: string) => ReturnType
    }
  }
}

/* eslint-disable @typescript-eslint/no-explicit-any -- markdown-it internals are untyped */

export const WikiLinkNode = Node.create<WikiLinkOptions>({
  name: 'wikiLink',
  group: 'inline',
  inline: true,
  atom: true,

  addOptions() {
    return { onNavigate: undefined }
  },

  addAttributes() {
    return {
      noteId: { default: null, parseHTML: el => el.getAttribute('data-note-id') },
    }
  },

  parseHTML() {
    return [{ tag: 'span[data-note-id]' }]
  },

  renderHTML({ HTMLAttributes }) {
    return ['span', mergeAttributes({ 'data-note-id': HTMLAttributes.noteId }, HTMLAttributes)]
  },

  addNodeView() {
    return ReactNodeViewRenderer(WikiLinkView)
  },

  addCommands() {
    return {
      insertWikiLink:
        (noteId: string) =>
        ({ commands }) =>
          commands.insertContent({ type: this.name, attrs: { noteId } }),
    }
  },

  addStorage() {
    return {
      markdown: {
        serialize(state: any, node: any) {
          state.write(`[[${node.attrs.noteId}]]`)
        },
        parse: {
          setup(markdownit: any) {
            markdownit.inline.ruler.push('wiki_link', (state: any) => {
              const src = state.src.slice(state.pos)
              const match = src.match(/^\[\[([^\]]+)\]\]/)
              if (!match) return false

              if (!state.env.wikiLinks) state.env.wikiLinks = []

              const token = state.push('wiki_link', '', 0)
              token.content = match[1]
              state.pos += match[0].length
              return true
            })

            markdownit.renderer.rules.wiki_link = (tokens: any[], idx: number) => {
              const noteId = tokens[idx].content
              return `<span data-note-id="${noteId}"></span>`
            }
          },
        },
      } satisfies MarkdownNodeSpec,
    }
  },
})
